import React from "react";
import { formatCurrency } from "../../lib/utils";

const sampleInvoice = {
    invoice_number: "FAC-2026-0001",
    client_name: "Exemple Client SAS",
    created_at: new Date().toISOString(),
    due_date: new Date(Date.now() + 30 * 86400000).toISOString(),
    items: [
        { description: "Conseil stratégique", quantity: 3, unit_price: 1500 },
        { description: "Développement web", quantity: 10, unit_price: 850 },
        { description: "Formation équipe", quantity: 1, unit_price: 2000 },
    ],
    subtotal: 15000,
    discount_percent: 5,
    discount_amount: 750,
    tax_amount: 2850,
    total: 17100,
    amount_paid: 0,
    status: "sent",
    notes: "Merci pour votre collaboration.",
};

const sampleQuote = {
    quote_number: "DEV-2026-001",
    client_name: "Exemple Client SAS",
    created_at: new Date().toISOString(),
    items: [
        { description: "Audit et analyse des besoins", quantity: 2, unit_price: 1200 },
        { description: "Conception et prototypage", quantity: 5, unit_price: 950 },
        { description: "Mise en production", quantity: 3, unit_price: 800 },
    ],
    subtotal: 9550,
    discount_percent: 0,
    discount_amount: 0,
    tax_amount: 1910,
    total: 11460,
    notes: "Proposition basée sur notre échange du 15 février.",
};

const formatPreviewDate = (dateStr) => {
    try {
        return new Date(dateStr).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    } catch {
        return "-";
    }
};

export const InvoicePreview = ({ billingSettings }) => {
    const company = billingSettings?.company_info || {};
    const paymentInfo = billingSettings?.payment_info || {};
    const paymentTerms = billingSettings?.payment_terms || {};
    const invoiceFooter = billingSettings?.invoice_footer || {};
    const template = billingSettings?.invoice_template || {};

    const primaryColor = template.primary_color || "#1E3A8A";
    const companyName = company.legal_name || company.commercial_name || "Votre Entreprise";
    const inv = sampleInvoice;
    const prefix = template.number_prefix || "FAC";
    const displayNumber = `${prefix}-2026-0001`;

    return (
        <div className="relative bg-muted/30 rounded-xl p-4 border border-border">
            <div className="absolute top-3 right-3 px-2 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary">
                Aperçu live
            </div>
            <div
                className="bg-white text-black mx-auto shadow-lg"
                style={{
                    fontFamily: "Arial, sans-serif",
                    width: "100%",
                    maxWidth: "520px",
                    padding: "24px",
                    fontSize: "9px",
                    lineHeight: "1.4",
                }}
            >
                {/* Header */}
                <div
                    className="flex justify-between items-start pb-3 mb-4"
                    style={{ borderBottom: `2px solid ${primaryColor}` }}
                >
                    <div>
                        <div className="font-bold text-lg" style={{ color: primaryColor }}>FACTURE</div>
                        <div className="font-semibold text-xs mt-0.5">{displayNumber}</div>
                    </div>
                    <div className="text-right">
                        {company.logo_url && template.show_logo !== false && (
                            <img src={company.logo_url} alt="" className="h-6 mb-1 ml-auto" />
                        )}
                        <div className="font-bold text-xs" style={{ color: primaryColor }}>{companyName}</div>
                        {company.address && <div className="text-gray-500">{company.address}</div>}
                        {company.email && <div className="text-gray-500">{company.email}</div>}
                        {company.phone && <div className="text-gray-500">{company.phone}</div>}
                        {company.business_number && <div className="text-gray-500">N°: {company.business_number}</div>}
                        {company.tax_number && <div className="text-gray-500">TVA: {company.tax_number}</div>}
                    </div>
                </div>

                {/* Info */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-50 p-2 rounded">
                        <div className="text-[7px] text-gray-400 uppercase font-semibold mb-1">Facturé à</div>
                        <div className="font-bold text-xs">{inv.client_name}</div>
                    </div>
                    <div className="bg-gray-50 p-2 rounded text-right">
                        <div className="text-[7px] text-gray-400 uppercase font-semibold mb-1">Date</div>
                        <div className="font-bold">{formatPreviewDate(inv.created_at)}</div>
                        <div className="text-[7px] text-gray-400 uppercase font-semibold mt-1">Échéance</div>
                        <div className="font-bold">{formatPreviewDate(inv.due_date)}</div>
                    </div>
                </div>

                {/* Items */}
                <table className="w-full mb-3" style={{ fontSize: "8px" }}>
                    <thead>
                        <tr style={{ backgroundColor: primaryColor }} className="text-white">
                            <th className="text-left p-1.5 font-semibold">Description</th>
                            <th className="text-center p-1.5 font-semibold w-8">Qté</th>
                            <th className="text-right p-1.5 font-semibold w-16">P.U.</th>
                            <th className="text-right p-1.5 font-semibold w-16">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {inv.items.map((item, i) => (
                            <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : ""}>
                                <td className="p-1.5">{item.description}</td>
                                <td className="text-center p-1.5">{item.quantity}</td>
                                <td className="text-right p-1.5 font-mono">{formatCurrency(item.unit_price)}</td>
                                <td className="text-right p-1.5 font-mono font-semibold">{formatCurrency(item.quantity * item.unit_price)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end mb-3">
                    <div style={{ width: "180px", fontSize: "8px" }}>
                        <div className="flex justify-between py-1 border-b">
                            <span>Sous-total HT</span>
                            <span className="font-mono">{formatCurrency(inv.subtotal)}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b text-green-600">
                            <span>Remise (5%)</span>
                            <span className="font-mono">-{formatCurrency(inv.discount_amount)}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b">
                            <span>TVA (20%)</span>
                            <span className="font-mono">{formatCurrency(inv.tax_amount)}</span>
                        </div>
                        <div
                            className="flex justify-between py-1.5 text-white px-2 rounded mt-1 font-bold"
                            style={{ backgroundColor: primaryColor, fontSize: "10px" }}
                        >
                            <span>Total TTC</span>
                            <span className="font-mono">{formatCurrency(inv.total)}</span>
                        </div>
                    </div>
                </div>

                {/* Payment Info */}
                <div className="p-2 rounded mb-3" style={{ backgroundColor: `${primaryColor}10` }}>
                    <div className="font-semibold uppercase mb-1" style={{ color: primaryColor, fontSize: "7px" }}>
                        Informations de paiement
                    </div>
                    {paymentInfo.beneficiary_name && <div>Bénéficiaire : {paymentInfo.beneficiary_name}</div>}
                    {paymentInfo.bank_name && <div>Banque : {paymentInfo.bank_name}</div>}
                    {paymentInfo.iban && <div>IBAN : {paymentInfo.iban}</div>}
                    {paymentInfo.bic && <div>BIC : {paymentInfo.bic}</div>}
                    {!paymentInfo.beneficiary_name && !paymentInfo.iban && (
                        <div>Virement bancaire à l'ordre de {companyName}</div>
                    )}
                    <div className="mt-0.5">Référence : {displayNumber}</div>
                </div>

                {/* Footer */}
                <div className="pt-2 border-t text-center text-gray-400" style={{ fontSize: "7px" }}>
                    {invoiceFooter.thank_you_message && <div>{invoiceFooter.thank_you_message}</div>}
                    {invoiceFooter.legal_mention && <div className="mt-0.5">{invoiceFooter.legal_mention}</div>}
                    {paymentTerms.late_penalty_enabled && (
                        <div className="mt-0.5">
                            {paymentTerms.late_penalty_text ||
                                `Pénalités de retard : ${paymentTerms.late_penalty_rate || 3}%`}
                        </div>
                    )}
                    {invoiceFooter.custom_text && <div className="mt-0.5">{invoiceFooter.custom_text}</div>}
                    {invoiceFooter.show_orkestria_mention !== false && (
                        <div className={`mt-0.5 ${invoiceFooter.orkestria_mention_discrete ? "text-[6px]" : ""}`}>
                            {companyName}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const QuotePreview = ({ billingSettings, quoteSettings }) => {
    const company = billingSettings?.company_info || {};
    const branding = quoteSettings?.branding || {};
    const signature = quoteSettings?.signature || {};
    const footer = quoteSettings?.footer || {};
    const introduction = quoteSettings?.introduction || {};
    const commercialTerms = quoteSettings?.commercial_terms || {};
    const clauses = quoteSettings?.clauses || {};
    const numbering = quoteSettings?.numbering || {};

    const primaryColor = branding.primary_color || "#1E3A8A";
    const companyName = company.legal_name || company.commercial_name || "Votre Entreprise";
    const q = sampleQuote;
    const prefix = numbering.prefix || "DEV";
    const displayNumber = `${prefix}-2026-001`;
    const validityDays = commercialTerms.validity_days || 30;

    return (
        <div className="relative bg-muted/30 rounded-xl p-4 border border-border">
            <div className="absolute top-3 right-3 px-2 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary">
                Aperçu live
            </div>
            <div
                className="bg-white text-black mx-auto shadow-lg"
                style={{
                    fontFamily: "Arial, sans-serif",
                    width: "100%",
                    maxWidth: "520px",
                    padding: "24px",
                    fontSize: "9px",
                    lineHeight: "1.4",
                }}
            >
                {/* Header */}
                <div
                    className="flex justify-between items-start pb-3 mb-4"
                    style={{ borderBottom: `2px solid ${primaryColor}` }}
                >
                    <div>
                        <div className="font-bold text-lg" style={{ color: primaryColor }}>DEVIS</div>
                        <div className="font-semibold text-xs mt-0.5">{displayNumber}</div>
                    </div>
                    <div className="text-right">
                        {company.logo_url && branding.show_logo !== false && (
                            <img src={company.logo_url} alt="" className="h-6 mb-1 ml-auto" />
                        )}
                        <div className="font-bold text-xs" style={{ color: primaryColor }}>{companyName}</div>
                        {company.address && <div className="text-gray-500">{company.address}</div>}
                        {company.email && <div className="text-gray-500">{company.email}</div>}
                        {company.phone && <div className="text-gray-500">{company.phone}</div>}
                        {company.business_number && <div className="text-gray-500">N°: {company.business_number}</div>}
                        {company.tax_number && <div className="text-gray-500">TVA: {company.tax_number}</div>}
                    </div>
                </div>

                {/* Info */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-50 p-2 rounded">
                        <div className="text-[7px] text-gray-400 uppercase font-semibold mb-1">Client</div>
                        <div className="font-bold text-xs">{q.client_name}</div>
                    </div>
                    <div className="bg-gray-50 p-2 rounded text-right">
                        <div className="text-[7px] text-gray-400 uppercase font-semibold mb-1">Date</div>
                        <div className="font-bold">{formatPreviewDate(q.created_at)}</div>
                        <div className="text-[7px] text-gray-400 uppercase font-semibold mt-1">Validité</div>
                        <div className="font-bold">{validityDays} jours</div>
                    </div>
                </div>

                {/* Introduction */}
                {introduction.default_text && (
                    <div className="mb-3 text-gray-600 italic">
                        {introduction.default_text.length > 120
                            ? introduction.default_text.substring(0, 120) + "..."
                            : introduction.default_text}
                    </div>
                )}

                {/* Items */}
                <table className="w-full mb-3" style={{ fontSize: "8px" }}>
                    <thead>
                        <tr style={{ backgroundColor: primaryColor }} className="text-white">
                            <th className="text-left p-1.5 font-semibold">Description</th>
                            <th className="text-center p-1.5 font-semibold w-8">Qté</th>
                            <th className="text-right p-1.5 font-semibold w-16">P.U.</th>
                            <th className="text-right p-1.5 font-semibold w-16">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {q.items.map((item, i) => (
                            <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : ""}>
                                <td className="p-1.5">{item.description}</td>
                                <td className="text-center p-1.5">{item.quantity}</td>
                                <td className="text-right p-1.5 font-mono">{formatCurrency(item.unit_price)}</td>
                                <td className="text-right p-1.5 font-mono font-semibold">{formatCurrency(item.quantity * item.unit_price)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end mb-3">
                    <div style={{ width: "180px", fontSize: "8px" }}>
                        <div className="flex justify-between py-1 border-b">
                            <span>Sous-total HT</span>
                            <span className="font-mono">{formatCurrency(q.subtotal)}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b">
                            <span>TVA (20%)</span>
                            <span className="font-mono">{formatCurrency(q.tax_amount)}</span>
                        </div>
                        <div
                            className="flex justify-between py-1.5 text-white px-2 rounded mt-1 font-bold"
                            style={{ backgroundColor: primaryColor, fontSize: "10px" }}
                        >
                            <span>Total TTC</span>
                            <span className="font-mono">{formatCurrency(q.total)}</span>
                        </div>
                    </div>
                </div>

                {/* Deposit */}
                {commercialTerms.deposit_required && commercialTerms.deposit_percentage && (
                    <div className="p-2 bg-amber-50 border border-amber-200 rounded mb-3">
                        <div className="font-semibold">
                            Acompte : {commercialTerms.deposit_percentage}% soit{" "}
                            {formatCurrency((q.total * commercialTerms.deposit_percentage) / 100)}
                        </div>
                        {commercialTerms.payment_terms && (
                            <div className="text-gray-500 mt-0.5">{commercialTerms.payment_terms}</div>
                        )}
                    </div>
                )}

                {commercialTerms.execution_delay && (
                    <div className="mb-2">
                        <strong>Délai :</strong> {commercialTerms.execution_delay}
                    </div>
                )}

                {/* Clauses */}
                {clauses.general_conditions && (
                    <div className="mb-2 text-gray-500" style={{ fontSize: "7px" }}>
                        <div className="uppercase font-semibold mb-0.5">Conditions Générales</div>
                        <div>
                            {clauses.general_conditions.length > 150
                                ? clauses.general_conditions.substring(0, 150) + "..."
                                : clauses.general_conditions}
                        </div>
                    </div>
                )}

                {/* Signature */}
                {(signature.signer_name || signature.signer_title) && (
                    <div className="flex justify-end mt-4">
                        <div className="text-right">
                            <div className="text-[7px] text-gray-400 uppercase font-semibold mb-1">Signature</div>
                            {signature.signature_image_url && (
                                <img src={signature.signature_image_url} alt="" className="h-8 mb-1 ml-auto" />
                            )}
                            {signature.signer_name && <div className="font-semibold">{signature.signer_name}</div>}
                            {signature.signer_title && <div className="text-gray-500">{signature.signer_title}</div>}
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="pt-2 mt-3 border-t text-center text-gray-400" style={{ fontSize: "7px" }}>
                    {footer.thank_you_message && <div>{footer.thank_you_message}</div>}
                    {footer.legal_mention && <div className="mt-0.5">{footer.legal_mention}</div>}
                    {footer.custom_text && <div className="mt-0.5">{footer.custom_text}</div>}
                    <div className="mt-0.5">Devis valable {validityDays} jours.</div>
                    {footer.show_orkestria_mention !== false && (
                        <div className={`mt-0.5 ${footer.orkestria_mention_discrete ? "text-[6px]" : ""}`}>
                            {companyName}
                        </div>
                    )}                </div>
            </div>
        </div>
    );
};
